/**
 * Ritual Service
 * Web-compatible service for managing daily rituals
 * Based on mobile app ritual features, adapted for Supabase
 */

import { getSupabase } from '@/lib/db/supabase';

// Get Supabase client (may be null if not configured)
// Use inline function calls instead of top-level const to avoid TDZ issues

export type RitualStage = 'prime' | 'learn' | 'voice' | 'do' | 'note' | 'done';
export type RitualMode = 'quick' | 'fluent';

export interface Ritual {
  id: string;
  user_id: string;
  date: string;
  mode: RitualMode;
  practice_type?: 'inner-work' | 'learning';
  custom_topic?: string;
  virtue?: string;
  topic_id?: string;
  content_key?: string;
  lens_ids?: string[];
  timers?: Record<string, number>;
  ended: boolean;
  current_stage?: RitualStage;
  stages?: Record<string, unknown>;
  oracle_metrics?: Record<string, unknown>;
  completed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateRitualInput {
  mode: RitualMode;
  practice_type?: 'inner-work' | 'learning';
  custom_topic?: string;
  virtue?: string;
  topic_id?: string;
  content_key?: string;
  lens_ids?: string[];
}

export interface UpdateRitualStageInput {
  stage: RitualStage;
  stage_data?: Record<string, unknown>;
  timers?: Record<string, number>;
}

export interface CompleteRitualInput {
  oracle_metrics?: Record<string, unknown>;
  stages?: Record<string, unknown>;
}

const STAGE_ORDER: RitualStage[] = ['prime', 'learn', 'voice', 'do', 'note', 'done'];
const QUICK_MODE_STAGES: RitualStage[] = ['prime', 'learn', 'voice', 'note', 'done'];

/**
 * Get next stage in sequence
 */
export function getNextStage(currentStage: RitualStage, mode: RitualMode): RitualStage | null {
  const stages = mode === 'quick' ? QUICK_MODE_STAGES : STAGE_ORDER;
  const currentIndex = stages.indexOf(currentStage);

  if (currentIndex >= 0 && currentIndex < stages.length - 1) {
    return stages[currentIndex + 1];
  }

  return null;
}

/**
 * Check if stage can be skipped
 */
export function canSkipStage(stage: RitualStage, mode: RitualMode): boolean {
  // Prime and Note should not be skipped
  if (stage === 'prime' || stage === 'note') {
    return false;
  }

  // Quick mode allows skipping Do stage (already excluded from quick mode)
  return true;
}

/**
 * Create a new ritual session
 * Falls back to local storage if not authenticated
 */
export async function createRitual(input: CreateRitualInput): Promise<Ritual> {
  // Check if Supabase is configured
  const client = getSupabase();
  if (!client) {
    // Supabase not configured - use local storage
    const { createLocalRitual } = await import('@/lib/storage/localRitual');
    return (await createLocalRitual(input)) as unknown as Ritual;
  }

  try {
    const {
      data: { user },
    } = await client.auth.getUser();

    if (!user) {
      // Fallback to local storage for guest mode
      const { createLocalRitual } = await import('@/lib/storage/localRitual');
      return (await createLocalRitual(input)) as unknown as Ritual;
    }

    const { data, error } = await client
      .from('rituals')
      .insert({
        user_id: user.id,
        mode: input.mode,
        practice_type: input.practice_type,
        custom_topic: input.custom_topic,
        virtue: input.virtue,
        topic_id: input.topic_id,
        content_key: input.content_key,
        lens_ids: input.lens_ids,
        current_stage: 'prime',
        ended: false,
      })
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to create ritual: ${error.message}`);
    }

    return data as Ritual;
  } catch {
    // Supabase error - use local storage
    const { createLocalRitual } = await import('@/lib/storage/localRitual');
    return (await createLocalRitual(input)) as unknown as Ritual;
  }
}

/**
 * Get active ritual (most recent non-ended ritual)
 * Falls back to local storage if not authenticated
 */
export async function getActiveRitual(): Promise<Ritual | null> {
  // Check if Supabase is configured
  const client = getSupabase();
  if (!client) {
    // Supabase not configured - use local storage
    const { getActiveLocalRitual } = await import('@/lib/storage/localRitual');
    return (await getActiveLocalRitual()) as unknown as Ritual | null;
  }

  try {
    const {
      data: { user },
    } = await client.auth.getUser();

    if (!user) {
      // Fallback to local storage
      const { getActiveLocalRitual } = await import('@/lib/storage/localRitual');
      return (await getActiveLocalRitual()) as unknown as Ritual | null;
    }

    const { data, error } = await client
      .from('rituals')
      .select('*')
      .eq('user_id', user.id)
      .eq('ended', false)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      throw new Error(`Failed to get active ritual: ${error.message}`);
    }

    return data as Ritual | null;
  } catch {
    // Supabase error - use local storage
    const { getActiveLocalRitual } = await import('@/lib/storage/localRitual');
    return (await getActiveLocalRitual()) as unknown as Ritual | null;
  }
}

/**
 * Get ritual by ID
 */
export async function getRitual(ritualId: string): Promise<Ritual | null> {
  const client = getSupabase();
  if (!client) {
    return null; // Supabase not configured
  }

  try {
    const {
      data: { user },
    } = await client.auth.getUser();

    if (!user) {
      return null;
    }

    const { data, error } = await client
      .from('rituals')
      .select('*')
      .eq('id', ritualId)
      .eq('user_id', user.id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // Not found
      }
      throw new Error(`Failed to get ritual: ${error.message}`);
    }

    return data as Ritual;
  } catch {
    return null;
  }
}

/**
 * Update ritual stage
 * Falls back to local storage if not authenticated
 */
export async function updateRitualStage(
  ritualId: string,
  input: UpdateRitualStageInput
): Promise<Ritual> {
  const client = getSupabase();
  if (!client) {
    // Supabase not configured - use local storage
    const { updateLocalRitualStage } = await import('@/lib/storage/localRitual');
    return (await updateLocalRitualStage(ritualId, input)) as unknown as Ritual;
  }

  try {
    const {
      data: { user },
    } = await client.auth.getUser();

    if (!user) {
      // Fallback to local storage
      const { updateLocalRitualStage } = await import('@/lib/storage/localRitual');
      return (await updateLocalRitualStage(ritualId, input)) as unknown as Ritual;
    }

    const updateData: Record<string, unknown> = {
      current_stage: input.stage,
      updated_at: new Date().toISOString(),
    };

    if (input.stage_data) {
      // Merge stage data into stages JSONB
      const { data: currentRitual } = await client
        .from('rituals')
        .select('stages')
        .eq('id', ritualId)
        .single();

      const currentStages = (currentRitual?.stages as Record<string, unknown>) || {};
      updateData.stages = {
        ...currentStages,
        [input.stage]: input.stage_data,
      };
    }

    if (input.timers) {
      updateData.timers = input.timers;
    }

    const { data, error } = await client
      .from('rituals')
      .update(updateData)
      .eq('id', ritualId)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update ritual stage: ${error.message}`);
    }

    return data as Ritual;
  } catch {
    // Supabase error - use local storage
    const { updateLocalRitualStage } = await import('@/lib/storage/localRitual');
    return (await updateLocalRitualStage(ritualId, input)) as unknown as Ritual;
  }
}

/**
 * Complete ritual
 * Falls back to local storage if not authenticated
 */
export async function completeRitual(
  ritualId: string,
  input?: CompleteRitualInput
): Promise<Ritual> {
  const client = getSupabase();
  if (!client) {
    // Supabase not configured - use local storage
    const { completeLocalRitual } = await import('@/lib/storage/localRitual');
    return (await completeLocalRitual(ritualId, input)) as unknown as Ritual;
  }

  try {
    const {
      data: { user },
    } = await client.auth.getUser();

    if (!user) {
      // Fallback to local storage
      const { completeLocalRitual } = await import('@/lib/storage/localRitual');
      return (await completeLocalRitual(ritualId, input)) as unknown as Ritual;
    }

    const updateData: Record<string, unknown> = {
      ended: true,
      current_stage: 'done',
      completed_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    if (input?.oracle_metrics) {
      updateData.oracle_metrics = input.oracle_metrics;
    }

    if (input?.stages) {
      updateData.stages = input.stages;
    }

    const { data, error } = await client
      .from('rituals')
      .update(updateData)
      .eq('id', ritualId)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to complete ritual: ${error.message}`);
    }

    return data as Ritual;
  } catch {
    // Supabase error - use local storage
    const { completeLocalRitual } = await import('@/lib/storage/localRitual');
    return (await completeLocalRitual(ritualId, input)) as unknown as Ritual;
  }
}

/**
 * Get user's ritual history
 */
export async function getRitualHistory(limit = 30): Promise<Ritual[]> {
  const client = getSupabase();
  if (!client) {
    return []; // Supabase not configured
  }

  try {
    const {
      data: { user },
    } = await client.auth.getUser();

    if (!user) {
      return [];
    }

    const { data, error } = await client
      .from('rituals')
      .select('*')
      .eq('user_id', user.id)
      .eq('ended', true)
      .order('completed_at', { ascending: false })
      .limit(limit);

    if (error) {
      throw new Error(`Failed to get ritual history: ${error.message}`);
    }

    return (data || []) as Ritual[];
  } catch {
    return [];
  }
}

/**
 * Get user's current streak
 * Falls back to local storage if not authenticated
 */
export async function getCurrentStreak(): Promise<number> {
  const client = getSupabase();
  if (!client) {
    // Supabase not configured - use local storage
    const { getLocalStreak } = await import('@/lib/storage/localRitual');
    return await getLocalStreak();
  }

  try {
    const {
      data: { user },
    } = await client.auth.getUser();

    if (!user) {
      // Fallback to local storage
      const { getLocalStreak } = await import('@/lib/storage/localRitual');
      return await getLocalStreak();
    }

    const { data, error } = await client
      .from('profiles')
      .select('current_streak')
      .eq('id', user.id)
      .single();

    if (error) {
      console.warn('Failed to get streak:', error.message);
      return 0;
    }

    return (data?.current_streak as number) || 0;
  } catch {
    // Supabase error - use local storage
    const { getLocalStreak } = await import('@/lib/storage/localRitual');
    return await getLocalStreak();
  }
}
